import * as Constants from '../actions/configuration';
import idConstants from '../data/idConstants';
import { accessRoles, rolesWithoutSellerApiCall } from '../data/appConstants';
import { trackPageView, initialization } from './analytics';
import DeviceBreakPoints from '../deviceBreakPoints';

export const sortByDisplayOrder = (list) => {
  return list?.sort(
    (attribute1, attribute2) =>
      parseInt(attribute1.displayOrder) - parseInt(attribute2.displayOrder)
  );
};

export const isValidJSONString = (str) => {
  try {
    JSON.parse(str);
  } catch (error) {
    return false;
  }
  return true;
};

export const camelToHyphen = (string) =>
  string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

export const capsWithSpaceToTitleCase = (string) => {
  let convertedString = '';
  string?.split(' ')?.forEach((word) => {
    if (word !== idConstants?.sidebarConstants?.apiProduct?.title)
      word = word?.toLowerCase();
    convertedString =
      convertedString + ' ' + word[0]?.toUpperCase() + word?.slice(1);
  });
  return convertedString;
};

export const getEnvironment = (env) => {
  switch (env) {
    case Constants.REACT_APP_ENVIRONMENT.DEV:
      return 'development';
    case Constants.REACT_APP_ENVIRONMENT.QA:
      return 'qa';
    case Constants.REACT_APP_ENVIRONMENT.QA_INT:
      return 'qa-internal';
    case Constants.REACT_APP_ENVIRONMENT.STAGE:
      return 'stage';
    case Constants.REACT_APP_ENVIRONMENT.PROD:
      return 'production';
    default:
      return 'development';
  }
};

export const encodeRequestorPayload = (requestorObject) => {
  try {
    return btoa(JSON.stringify(requestorObject));
  } catch (error) {
    return null;
  }
};

export const isClientWidthExceeded = (id) => {
  const event = document.getElementById(id);
  return event?.scrollWidth > event?.clientWidth;
};

export const capitalizeFirstLetter = (str) => {
  return str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase();
};

export const utf8Encode = (input) => {
  return encodeURIComponent(input).replace(/!/g, '%21');
};

export const getPricingPlanStatus = (planStatus, productStatusId) => {
  let status = {};
  switch (planStatus) {
    case idConstants.drafted.description:
      status.type = 'DYNAMIC_FORM.SHARED.DRAFT';
      status.cardType = 'drafted-card';
      break;
    case idConstants.submitted.description:
      status.type = 'DYNAMIC_FORM.TENANT_STATUS.AWAITING_APPROVAL';
      status.cardType = 'submitted-card';
      break;
    case idConstants.publishInProgress.description:
      status.type =
        productStatusId === idConstants.published.statusId
          ? 'DYNAMIC_FORM.SHARED.PUBLISH_IN_PROGRESS'
          : 'DYNAMIC_FORM.SHARED.APPROVED';
      status.cardType =
        productStatusId === idConstants.published.statusId
          ? 'submitted-card'
          : 'published-card';
      break;
    case idConstants.published.description:
      status.type =
        productStatusId === idConstants.published.statusId
          ? 'DYNAMIC_FORM.TENANT_STATUS.PUBLISHED'
          : 'DYNAMIC_FORM.SHARED.APPROVED';
      status.cardType = 'published-card';
      break;
    case idConstants.rejected.description:
      status.type = 'DYNAMIC_FORM.SHARED.REJECTED';
      status.cardType = 'rejected-card';
      break;
    default:
      status.type = planStatus ?? '';
      status.cardType = 'default-card';
  }
  return status;
};

export const comparePlanDetails = (item1, item2) => {
  const freePlan = idConstants.pricingPlanType.freePlan;
  const customPlans = idConstants.pricingPlanType.customPlans;

  const planType1 = item1.planDetails?.planType;
  const planType2 = item2.planDetails?.planType;

  if (planType1 === freePlan) return -1;
  if (planType2 === freePlan) return 1;
  if (planType1 === customPlans) return 1;
  if (planType2 === customPlans) return -1;
  return 0;
};

export const addAttributesByXPath = (obj, newAttribute) => {
  const parentXPath = newAttribute.xpath.slice(
    0,
    newAttribute.xpath.lastIndexOf('/')
  );
  if (obj.xpath === parentXPath) {
    if (!obj.attributes) {
      obj.attributes = [];
    }
    if (obj.attributes?.some((attribute) => attribute.name === newAttribute.name))
      return;
    obj.attributes.push(newAttribute);
  }

  if (obj.sections?.length) {
    obj.sections.forEach((section) => addAttributesByXPath(section, newAttribute));
  }

  if (obj.groups?.length) {
    obj.groups.forEach((group) => addAttributesByXPath(group, newAttribute));
  }

  if (obj.attributes?.length) {
    obj.attributes.forEach((attribute) =>
      addAttributesByXPath(attribute, newAttribute)
    );
  }
};

export const modifyAttributeProperty = (obj, name, propertyPath, newValue) => {
  const setNestedProperty = (obj, path, value) => {
    const properties = path.split('.');
    let current = obj;

    for (let i = 0; i < properties.length - 1; i++) {
      if (!current[properties[i]]) {
        current[properties[i]] = {};
      }
      current = current[properties[i]];
    }

    current[properties[properties.length - 1]] = value;
  };

  if (obj.attributes?.length) {
    obj.attributes.forEach((attribute) => {
      if (attribute.name === name) {
        setNestedProperty(attribute, propertyPath, newValue);
      }
    });
  }

  if (obj.steps?.length) {
    obj.steps.forEach((step) =>
      modifyAttributeProperty(step, name, propertyPath, newValue)
    );
  }

  if (obj.sections?.length) {
    obj.sections.forEach((section) =>
      modifyAttributeProperty(section, name, propertyPath, newValue)
    );
  }

  if (obj.groups?.length) {
    obj.groups.forEach((group) =>
      modifyAttributeProperty(group, name, propertyPath, newValue)
    );
  }

  if (obj.attributes?.length) {
    obj.attributes.forEach((attribute) =>
      modifyAttributeProperty(attribute, name, propertyPath, newValue)
    );
  }
};

const getNestedProperty = (obj, path) => {
  const properties = path.split('.');
  return properties.reduce(
    (current, property) =>
      current?.hasOwnProperty(property) ? current[property] : undefined,
    obj
  );
};

const checkAttribute = (attribute, name, propertyPath) => {
  if (attribute.name === name) {
    const value = getNestedProperty(attribute, propertyPath);
    return value !== undefined ? value : undefined;
  }
};

export const getAttributeProperty = (obj, name, propertyPath) => {
  const childKeys = ['steps', 'sections', 'groups', 'attributes'];

  if (obj.attributes?.length) {
    for (const attribute of obj.attributes) {
      const value = checkAttribute(attribute, name, propertyPath);
      return value;
    }
  }

  for (const key of childKeys) {
    const children = obj[key];
    if (children?.length) {
      for (const child of children) {
        const value = getAttributeProperty(child, name, propertyPath);
        if (value !== undefined) return value;
      }
    }
  }

  return undefined;
};

export const getCookie = (cookieName) => {
  const getCookies = decodeURIComponent(document.cookie).split(';');
  for (const [index, value] of getCookies.entries()) {
    if (getCookies[index].split('=')[0].indexOf(cookieName) !== -1) {
      //MS-19526 (Adding Google Analytics)
      initialization();
      trackPageView(window.location.pathname);
      return value.split('=')[1];
    }
  }
  return undefined;
};

export function formatMessage(template, ...values) {
  return template?.replace(/{(\d+)}/g, (match, index) => values[index] || match);
}

export const getDeviceClass = () => {
  const { isMediumDevice, isTablet, isDesktopDevice, isMobileDevice, isPhone } =
    DeviceBreakPoints();

  if (isMediumDevice || isMobileDevice || isPhone) {
    return 'mobile';
  } else if (isTablet) {
    return 'tablet';
  } else if (isDesktopDevice) {
    return 'desktop-small';
  } else {
    return 'desktop-large';
  }
};

export const setUserRoleFromHierarchy = (dispatch, userData) => {
  const roles = userData?.roles;
  if (!roles || !Array.isArray(roles)) {
    return;
  }

  // Add new roles at the top of the roleHierarchy array (highest priority first) with condition
  const roleHierarchy = [
    {
      role: accessRoles.usp_admin,
      condition: () =>
        roles.includes(accessRoles.usp_admin) && roles.includes(accessRoles.seller)
    },
    {
      role: accessRoles.csm,
      condition: () => roles.includes(accessRoles.csm)
    },
    {
      role: accessRoles.reseller,
      condition: () =>
        roles.includes(accessRoles.seller) && roles.includes(accessRoles.reseller)
    },
    {
      role: accessRoles.sales_rep,
      condition: () =>
        roles.includes(accessRoles.seller) && roles.includes(accessRoles.sales_rep)
    },
    {
      role: accessRoles.seller,
      condition: () => roles.includes(accessRoles.seller)
    }
  ];

  // Find the first role that matches the condition
  const selectedRole = roleHierarchy.find(({ condition }) => condition());

  if (selectedRole) {
    dispatch({
      type: 'SET_CURRENT_USER',
      payload: { ...userData, roles: [selectedRole.role] }
    });
  } else {
    dispatch({
      type: 'SET_CURRENT_USER',
      payload: { ...userData, roles: roles }
    });
  }
};

export const checkUserRoles = (userRoles, rolesToCheck) => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }
  return userRoles?.includes(rolesToCheck);
};

export const AuthCallbackNavigation = (props, roles) => {
  const dashboardRoles = [
    accessRoles.reseller,
    accessRoles.sales_rep,
    accessRoles.seller,
    accessRoles.usp_admin,
    accessRoles.guest
  ];
  if (roles?.includes(accessRoles.csm)) {
    props.history.push('/dashboard/place-order');
  } else if (dashboardRoles?.some((role) => roles?.includes(role))) {
    props.history.push('/dashboard');
  }
};

export const handleOrderNavigation = (role, link) => {
  if (role === accessRoles.csm) {
    return '/dashboard/place-order';
  } else {
    return link;
  }
};

export const sellerApiRestrictedRoles = (userRoles) => {
  return userRoles?.filter((role) => rolesWithoutSellerApiCall.includes(role));
};

export const getBillingTypeCodeFromPlanType = (planDetails) => {
  const planInfo =
    planDetails?.planType +
    '_' +
    planDetails?.paymentType +
    '_' +
    planDetails?.paymentModePref;
  if (planDetails?.occurrence === '1') {
    switch (planInfo) {
      case idConstants.pricingPlanTypes.usageBasedPostpaidEmandate:
      case idConstants.pricingPlanTypes.flatRatePostpaidEmandate:
        return idConstants.oneTimePostBillingType.billingTypeCode;
      case idConstants.pricingPlanTypes.usageBasedPostpaidInvoice:
      case idConstants.pricingPlanTypes.flatRatePostpaidInvoice:
        return idConstants.oneTimeInvoiceBillingType.billingTypeCode;
      case idConstants.pricingPlanTypes.flatRatePrepaidNetBanking:
        return idConstants.oneTimeBillingType.billingTypeCode;
      case idConstants.pricingPlanTypes.freeNAInvoice:
        return idConstants.freePlanBillingType.billingTypeCode;
      case idConstants.pricingPlanTypes.customPlansNANA:
        return idConstants.contactSalesBillingType.billingTypeCode;
      default:
        return idConstants.oneTimePostBillingType.billingTypeCode;
    }
  } else {
    switch (planInfo) {
      case idConstants.pricingPlanTypes.usageBasedPostpaidEmandate:
      case idConstants.pricingPlanTypes.flatRatePostpaidEmandate:
        return idConstants.recurringBillingType.billingTypeCode;
      case idConstants.pricingPlanTypes.usageBasedPostpaidInvoice:
      case idConstants.pricingPlanTypes.flatRatePostpaidInvoice:
        return idConstants.recurringInvoiceBillingType.billingTypeCode;
      case idConstants.pricingPlanTypes.customPlansNANA:
        return idConstants.contactSalesBillingType.billingTypeCode;
      default:
        return idConstants.recurringBillingType.billingTypeCode;
    }
  }
};

/**
 * Fetches the sellers list with the provided parameters
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} getSellersList - Action creator for fetching sellers list
 * @param {Function} getRegionCode - Function to get region code
 * @param {number} currentPage - Current page number
 * @param {number} rowsPerPage - Number of rows per page
 * @param {string} statusFilter - Status filter value
 * @param {string} tenantId - Tenant ID for X-tenant header
 * @returns {Promise} Promise from the dispatch action
 */
export const fetchSellersListUtil = (
  dispatch,
  getSellersList,
  getRegionCode,
  currentPage,
  rowsPerPage,
  statusFilter,
  tenantId
) => {
  const regionCode = getRegionCode();
  return dispatch(
    getSellersList(regionCode, currentPage, rowsPerPage, statusFilter, {
      'X-tenant': tenantId
    })
  );
};
